import {
  Button,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Text,
} from "@chakra-ui/react";
import { FunctionComponent, useState } from "react";
import {
  showErrorToast,
  showInfoToast,
  showSuccessToast,
} from "../../../utilities/toasts";
import { useCooldown } from "../../../hooks/useCooldown";

export interface VerificationDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onResend: () => Promise<void>;
}

export const RESEND_COOLDOWN_SECONDS = 120;
export const VERIFY_EMAIL_STORAGE_KEY = "verify_email_cooldown";

const VerificationDrawer: FunctionComponent<VerificationDrawerProps> = ({
  isOpen,
  onClose,
  onResend,
}) => {
  const { cooldown, startCooldown } = useCooldown(
    VERIFY_EMAIL_STORAGE_KEY,
    RESEND_COOLDOWN_SECONDS
  );
  const [isResending, setIsResending] = useState(false);

  const handleResend = async () => {
    if (cooldown > 0 || isResending) return;
    setIsResending(true);
    try {
      await onResend();
      showSuccessToast("Лист надіслано повторно.");
      startCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (error: any) {
      if (error?.code === "auth/too-many-requests") {
        showInfoToast("Забагато спроб. Спробуйте пізніше.");
        startCooldown(RESEND_COOLDOWN_SECONDS);
      } else {
        showErrorToast("Не вдалося надіслати лист. Спробуйте пізніше.");
      }
    } finally {
      setIsResending(false);
    }
  };

  return (
    <Drawer isOpen={isOpen} placement="bottom" onClose={onClose}>
      <DrawerOverlay />
      <DrawerContent>
        <DrawerBody py="2.5rem">
          <DrawerHeader padding="0">
            <Text
              marginBottom={{ base: "0.5rem", md: "1rem" }}
              lineHeight={{ base: "1.5rem", sm: "auto" }}
              fontSize={{
                base: "1.5rem",
                md: "1.75rem",
                xl: "2.25rem",
              }}
              variant="link"
              fontWeight={800}
              fontFamily="GaretHeavy"
              textAlign="center"
            >
              Лист для підтвердження пошти надіслано
            </Text>
          </DrawerHeader>
          <Flex flexDirection="column" alignItems="center">
            <Text
              textAlign="center"
              size={{ base: "md", sm: "xs", md: "sm", xl: "md" }}
              marginBottom={{ base: "1rem", md: "1.5rem" }}
            >
              Ми надіслали лист для підтвердження на вашу електронну адресу.
              Перевірте пошту і перейдіть за посиланням у листі.
              Після підтвердження ви зможете увійти в акаунт.
            </Text>
            <Flex
              gap={{ base: "0.75rem", md: "1rem" }}
              flexDirection={{ base: "column", sm: "row" }}
              width={{ base: "100%", sm: "auto" }}
            >
              <Button
                onClick={handleResend}
                isLoading={isResending}
                isDisabled={cooldown > 0 || isResending}
                variant="outline"
                borderColor="orange"
                color="orange"
                _hover={{ backgroundColor: "orange", color: "white" }}
                borderRadius={{ base: "0.5rem", sm: "0.35rem", xl: "0.5rem" }}
                px={{ base: "2rem", xl: "3rem" }}
                py={{ base: "0.75rem", xl: "1rem" }}
                height="fit-content"
              >
                <Text
                  fontWeight={600}
                  size={{ base: "sm", sm: "xs", md: "sm", xl: "md" }}
                >
                  {cooldown > 0
                    ? `Надіслати ще раз (${cooldown}с)`
                    : "Надіслати ще раз"}
                </Text>
              </Button>
              <Button
                onClick={onClose}
                backgroundColor="orange"
                _hover={{ backgroundColor: "black" }}
                borderRadius={{ base: "0.5rem", sm: "0.35rem", xl: "0.5rem" }}
                px={{ base: "2rem", xl: "3rem" }}
                py={{ base: "0.75rem", xl: "1rem" }}
                height="fit-content"
                transition="all 0.3s"
              >
                <Text
                  fontWeight={600}
                  color="white"
                  size={{ base: "sm", sm: "xs", md: "sm", xl: "md" }}
                >
                  Зрозуміло
                </Text>
              </Button>
            </Flex>
          </Flex>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

export default VerificationDrawer;
